import type { CancelToken } from 'axios';
import {
  buffers, type Channel,
} from 'redux-saga';
import {
  actionChannel,
  call, put, race, take,
} from 'redux-saga/effects';
import { DEBUG_FETCH_ALL_FILLS_FOR_BOUND_LIMITED_BOUNDARIES } from '~/_shared/constants/debugConstants';
import type { LatLngBounds } from '~/_shared/types/latLng';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import type { ArrayElement } from '~/_shared/utils/array/array.helpers';
import {
  createUuid, type Uuid,
} from '~/_shared/utils/createUuid';
import { select } from '~/_shared/utils/saga/effects';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { boundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { isBoundaryGroupRenderable } from '~/store/boundaryItems/boundaryItems.helpers';
import { waitForBoundaryGroups } from '~/store/boundaryItems/boundaryItems.sagas';
import {
  boundaryTerritoryDetailsInvalidate,
  boundaryTerritoryDetailsZoomedLoadingError,
  boundaryTerritoryDetailsZoomedLoadingStart, boundaryTerritoryDetailsZoomedLoadingSuccess,
  boundaryTerritoryGroupsFetchDetailsRequest,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.actionCreators';
import { getBtgExtraDataQuery } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.helpers';
import {
  type BoundaryTerritoryGroupDetailsRequestQueryModel, getBoundaryTerritoryGroupDetails,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.repository';
import type { BoundaryTerritoryGroupDetails } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.types';
import {
  activeBoundaryTerritoryGroupsSelector, boundaryTerritoryGroupsSelector, primaryBoundaryGroupIdSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { mapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { primarySpreadsheetIdSelector } from '~/store/mapInfo/mapInfo.selectors';
import { waitForMapSettingsReady } from '~/store/mapSettings/ready/mapSettingsReady.sagas.helpers';
import {
  type BoundaryFilters,
  boundaryFiltersSelector,
} from '~/store/mapSettings/toolsState/boundaryTerritoryGroups/mapSettingsBTGToolsState.selectors';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import type { FilterTreeItemRequest } from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { getStoreFilterTree } from '~/store/spreadsheetData/filterTree/spreadsheetDataFilterTree.helpers';
import { boundaryTerritoryGroupActivate } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { mapComponentLastBoundsChanged } from '../frontendState/mapComponent/mapComponent.actionCreators';

export function* watchForLoadingOfZoomedInBoundaryDetails() {
  const activeFillChangeActions = DEBUG_FETCH_ALL_FILLS_FOR_BOUND_LIMITED_BOUNDARIES ? [] : [boundaryTerritoryGroupActivate];

  const mapBoundsChannel: Channel<Action> = yield actionChannel([
    boundaryTerritoryGroupsFetchDetailsRequest,
    mapComponentLastBoundsChanged,
    ...activeFillChangeActions],
  buffers.sliding(1));

  while (true) {
    const action: Action = yield take(mapBoundsChannel);
    yield call(waitForMapSettingsReady);
    yield call(waitForBoundaryGroups);

    const clientId: number | null = yield select<number | null>(clientIdSelector);
    const boundaryGroups: ReadonlyArray<BoundaryGroup> = yield select(boundaryGroupsSelector);
    const primaryBoundaryGroupId: ReturnType<typeof primaryBoundaryGroupIdSelector> = yield select(primaryBoundaryGroupIdSelector);
    const spreadsheetId: number | null = yield select<number | null>(primarySpreadsheetIdSelector);
    const mapBounds: ReturnType<typeof mapComponentLastBoundsSelector> = yield select(mapComponentLastBoundsSelector);
    const boundaryFilters: BoundaryFilters = yield select<BoundaryFilters>(boundaryFiltersSelector);

    const BTGs: ReadonlyArray<BoundaryTerritoryGroup> = yield select(
      DEBUG_FETCH_ALL_FILLS_FOR_BOUND_LIMITED_BOUNDARIES
        ? boundaryTerritoryGroupsSelector
        : activeBoundaryTerritoryGroupsSelector
    );

    if ((clientId === null) || (spreadsheetId === null) || !mapBounds) {
      continue;
    }

    const filteredBTGs = filterBTGsWithAppropriateZoomLevel(BTGs, boundaryGroups, mapBounds.zoomLevel, boundaryFilters)
      .filter(btg => {
        return !btg.settings.hideFillWhenInactive || primaryBoundaryGroupId === btg.boundaryGroupId;
      });

    if (filteredBTGs.length === 0) {
      continue;
    }

    if (boundaryTerritoryGroupActivate.match(action)) {
      // For active BTG switch, we only react to the relevant ones.
      if (!filteredBTGs.some(btg => btg.boundaryTerritoryGroupId === action.payload.activeBTG.boundaryTerritoryGroupId)) {
        continue;
      }
    }

    // Buffer any invalidate actions dispatched during preparatory work so the race catches it immediately.
    const invalidateChannel: Channel<Action> = yield actionChannel([boundaryTerritoryDetailsInvalidate], buffers.sliding(1));

    try {
      const btgWithQueryId = new Map<Uuid, BoundaryTerritoryGroup>(
        filteredBTGs.map((btg: BoundaryTerritoryGroup) => [createUuid(), btg])
      );

      let combinedFilterTrees: FilterTreeItemRequest | null = null;
      try {
        combinedFilterTrees = yield call(getStoreFilterTree);
      }
      catch (e) {
        yield put(boundaryTerritoryDetailsZoomedLoadingError(e));
        continue;
      }

      const requests = prepareBTGDetailsRequests(
        btgWithQueryId,
        spreadsheetId,
        combinedFilterTrees,
        mapBounds.bounds.getBounds(),
      );

      const cancelTokenSource = createCancelToken();

      const { invalidated } = yield race({
        completed: call(fetchAndDispatchBTGDetails, clientId, requests, btgWithQueryId, cancelTokenSource.token),
        invalidated: take(invalidateChannel),
      });
      // If invalidated wins, abort the in-flight HTTP request
      if (invalidated) {
        cancelTokenSource.cancel();
        continue;
      }
    }
    finally {
      invalidateChannel.close();
    }
  }
}

function filterBTGsWithAppropriateZoomLevel(
  activeBTGs: ReadonlyArray<BoundaryTerritoryGroup>,
  boundaryGroups: ReadonlyArray<BoundaryGroup>,
  zoomLevel: number,
  boundaryFilters: BoundaryFilters,
): ReadonlyArray<BoundaryTerritoryGroup> {
  return activeBTGs.filter(btg => {
    const bg = boundaryGroups.find(group => group.id === btg.boundaryGroupId);

    if (!bg) {
      return false;
    }

    const supportsCurrentZoom = bg.zoomLevels.some(level => level <= zoomLevel);
    const requiresZoom = !bg.zoomLevels.includes(0);

    return supportsCurrentZoom && requiresZoom && isBoundaryGroupRenderable(zoomLevel, bg, btg, boundaryFilters);
  });
}

function prepareBTGDetailsRequests(
  btgWithQueryId: Map<Uuid, BoundaryTerritoryGroup>,
  spreadsheetId: number,
  combinedFilterTrees: FilterTreeItemRequest | null,
  bounds: LatLngBounds,
): BoundaryTerritoryGroupDetailsRequestQueryModel[] {
  const requests: BoundaryTerritoryGroupDetailsRequestQueryModel[] = [];
  btgWithQueryId.forEach((btg, queryId) => {
    requests.push({
      boundaryTerritoryGroup: { ...btg },
      boundaryTerritoryGroupQueryId: queryId,
      filter: btg.settings.ignoreFilters || !combinedFilterTrees?.filterTree ? null : {
        filter_tree: combinedFilterTrees.filterTree,
      },
      boundingBox: bounds,
      extraData: getBtgExtraDataQuery(btg, spreadsheetId),
    });
  });
  return requests;
}

function* fetchAndDispatchBTGDetails(
  clientId: number,
  requests: BoundaryTerritoryGroupDetailsRequestQueryModel[],
  btgWithQueryId: Map<Uuid, BoundaryTerritoryGroup>,
  cancelToken: CancelToken,
) {
  yield put(boundaryTerritoryDetailsZoomedLoadingStart());

  try {
    const response: Map<Uuid, BoundaryTerritoryGroupDetails> = yield call(
      getBoundaryTerritoryGroupDetails, clientId, requests, cancelToken
    );

    const actionResults: Array<ArrayElement<Parameters<typeof boundaryTerritoryDetailsZoomedLoadingSuccess>[0]>> = [];

    for (const [queryId, details] of response.entries()) {
      const btg = btgWithQueryId.get(queryId);

      if (btg) {
        actionResults.push({
          boundaryTerritoryGroupId: btg.boundaryTerritoryGroupId,
          details,
        });
      }
    }

    yield put(boundaryTerritoryDetailsZoomedLoadingSuccess(actionResults));
  }
  catch (e) {
    yield put(boundaryTerritoryDetailsZoomedLoadingError(e));
  }
}
